import type { ReactNode } from 'react';
import { cn } from '../../lib/utils';

export interface ChatBubbleProps {
  role: 'user' | 'assistant';
  children: ReactNode;
  streaming?: boolean;
  className?: string;
}

/**
 * InkFinity's signature chat surface. Two-tone (terracotta user / surface assistant),
 * 12px radius with 4px attachment corner. Brand differentiator — must stay consistent.
 */
export function ChatBubble({ role, children, streaming = false, className }: ChatBubbleProps) {
  const isUser = role === 'user';

  return (
    <div
      className={cn(
        'max-w-prose px-4 py-3 text-sm leading-relaxed',
        'transition-opacity duration-base',
        isUser
          ? 'ml-auto rounded-2xl rounded-tr-md bg-brand-400 text-white'
          : 'mr-auto rounded-2xl rounded-tl-md border border-border-subtle bg-bg-surface text-text-primary',
        className
      )}
      role="article"
      aria-label={`${role === 'user' ? 'You' : 'AI'} message`}
    >
      {children}
      {streaming && !isUser && (
        <span
          className="ml-1 inline-block h-3.5 w-1.5 rounded-sm bg-brand-400 align-[-3px]"
          aria-hidden
        />
      )}
    </div>
  );
}

/**
 * Chat avatar — circular gradient for AI, neutral surface for user.
 */
export function ChatAvatar({
  role,
  children,
}: {
  role: 'user' | 'assistant';
  children?: ReactNode;
}) {
  return (
    <div
      className={cn(
        'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-xs font-semibold',
        role === 'user'
          ? 'bg-neutral-300 text-text-primary'
          : 'bg-gradient-to-br from-brand-300 to-brand-500 font-serif text-base text-white'
      )}
      aria-hidden
    >
      {children ?? (role === 'user' ? 'U' : 'I')}
    </div>
  );
}
